import { Event, request } from '@mirai/data-sources';
import { useCallback, useRef } from 'react';

import { EVENT, METRICS_EVENTS } from '../../../../helpers';
import { getClientFingerprint } from '../../../helpers';
import { getAllowedOpenUrl, getAllowedOpenUrlPrefix } from '../helpers';

const XAI_REALTIME_URL = 'wss://api.x.ai/v1/realtime';
const DEFAULT_MODEL = 'grok-voice-latest';
const SAMPLE_RATE = 24000;
const PROCESSOR_BUFFER_SIZE = 2048;

const float32ToInt16 = (float32) => {
  const int16 = new Int16Array(float32.length);
  for (let i = 0; i < float32.length; i += 1) {
    const sample = Math.max(-1, Math.min(1, float32[i]));
    int16[i] = sample < 0 ? sample * 0x8000 : sample * 0x7fff;
  }
  return int16;
};

const int16ToFloat32 = (int16) => {
  const float32 = new Float32Array(int16.length);
  for (let i = 0; i < int16.length; i += 1) {
    float32[i] = int16[i] / 0x8000;
  }
  return float32;
};

const base64FromInt16 = (int16) => {
  const bytes = new Uint8Array(int16.buffer, int16.byteOffset, int16.byteLength);
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
  }
  return btoa(binary);
};

const int16FromBase64 = (b64) => {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Int16Array(bytes.buffer, bytes.byteOffset, Math.floor(bytes.byteLength / 2));
};

const extractAudioDelta = (payload) => {
  // xAI docs show response.output_audio.delta with delta: { type: 'audio_delta', audio: 'base64' }.
  // Stay defensive in case the server returns a plain string in delta or a top-level audio field.
  if (typeof payload?.delta === 'string') return payload.delta;
  if (typeof payload?.delta?.audio === 'string') return payload.delta.audio;
  if (typeof payload?.audio === 'string') return payload.audio;
  return null;
};

const useXaiRealtimeSession = () => {
  const wsRef = useRef(null);
  const audioContextRef = useRef(null);
  const inputSourceRef = useRef(null);
  const scriptProcessorRef = useRef(null);
  const inputAnalyserRef = useRef(null);
  const outputAnalyserRef = useRef(null);
  const inputMuteGainRef = useRef(null);
  const localStreamRef = useRef(null);
  const microphoneEnabledRef = useRef(false);
  const playbackTimeRef = useRef(0);
  const activePlaybackSourcesRef = useRef(new Set());
  const inputFrequencyDataRef = useRef(null);
  const outputFrequencyDataRef = useRef(null);
  const audioAnalyzersRef = useRef({
    inputAnalyser: null,
    outputAnalyser: null,
    getInputByteFrequencyData: () => null,
    getOutputByteFrequencyData: () => null,
  });
  const pendingSessionConfigRef = useRef(null);
  const sessionAppliedRef = useRef(false);
  const introSentRef = useRef(false);
  const pendingUserTranscriptRef = useRef('');
  const handledOpenUrlCallsRef = useRef(new Set());

  const cleanupSession = useCallback(() => {
    const ws = wsRef.current;
    if (ws) {
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) ws.close();
      wsRef.current = null;
    }

    if (scriptProcessorRef.current) {
      scriptProcessorRef.current.onaudioprocess = null;
      scriptProcessorRef.current.disconnect();
      scriptProcessorRef.current = null;
    }
    if (inputSourceRef.current) {
      inputSourceRef.current.disconnect();
      inputSourceRef.current = null;
    }
    if (inputMuteGainRef.current) {
      inputMuteGainRef.current.disconnect();
      inputMuteGainRef.current = null;
    }
    if (inputAnalyserRef.current) {
      inputAnalyserRef.current.disconnect();
      inputAnalyserRef.current = null;
    }
    if (outputAnalyserRef.current) {
      outputAnalyserRef.current.disconnect();
      outputAnalyserRef.current = null;
    }

    activePlaybackSourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch {
        // already stopped
      }
    });
    activePlaybackSourcesRef.current = new Set();

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach((track) => track.stop());
      localStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    audioAnalyzersRef.current.inputAnalyser = null;
    audioAnalyzersRef.current.outputAnalyser = null;
    audioAnalyzersRef.current.getInputByteFrequencyData = () => null;
    audioAnalyzersRef.current.getOutputByteFrequencyData = () => null;
    inputFrequencyDataRef.current = null;
    outputFrequencyDataRef.current = null;
    microphoneEnabledRef.current = false;
    playbackTimeRef.current = 0;
    pendingSessionConfigRef.current = null;
    sessionAppliedRef.current = false;
    introSentRef.current = false;
    pendingUserTranscriptRef.current = '';
    handledOpenUrlCallsRef.current = new Set();
  }, []);

  const stopPendingPlayback = useCallback(() => {
    activePlaybackSourcesRef.current.forEach((source) => {
      try {
        source.stop();
      } catch {
        // already stopped
      }
    });
    activePlaybackSourcesRef.current = new Set();
    if (audioContextRef.current) playbackTimeRef.current = audioContextRef.current.currentTime;
  }, []);

  const scheduleAudioPlayback = useCallback((audioBase64) => {
    const context = audioContextRef.current;
    const analyser = outputAnalyserRef.current;
    if (!context || !analyser) return;
    const int16 = int16FromBase64(audioBase64);
    if (!int16.length) return;
    const float32 = int16ToFloat32(int16);
    const buffer = context.createBuffer(1, float32.length, SAMPLE_RATE);
    buffer.copyToChannel(float32, 0);
    const source = context.createBufferSource();
    source.buffer = buffer;
    source.connect(analyser);
    const startAt = Math.max(context.currentTime, playbackTimeRef.current);
    source.start(startAt);
    playbackTimeRef.current = startAt + buffer.duration;
    activePlaybackSourcesRef.current.add(source);
    source.onended = () => {
      activePlaybackSourcesRef.current.delete(source);
    };
  }, []);

  const sendJson = useCallback((payload) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return false;
    ws.send(JSON.stringify(payload));
    return true;
  }, []);

  const sendIntro = useCallback(
    ({ assistantName, locale }) => {
      if (introSentRef.current) return;
      const localeHint = locale || 'en-US';
      const text = `Respond in the language that matches the locale code "${localeHint}". Introduce yourself using the following sentence, translated into that language: "Hello, I am ${assistantName}, your virtual assistant. I can help you with information about our services and facilities. What would you like to know today?"`;

      sendJson({
        type: 'conversation.item.create',
        item: {
          type: 'message',
          role: 'user',
          content: [{ type: 'input_text', text }],
        },
      });
      sendJson({ type: 'response.create' });
      introSentRef.current = true;
    },
    [sendJson],
  );

  const handleFunctionCall = useCallback(
    (payload) => {
      const { name } = payload;
      const callId = payload.call_id;
      if (name !== 'open_url' || !callId) return;
      if (handledOpenUrlCallsRef.current.has(callId)) return;
      handledOpenUrlCallsRef.current.add(callId);

      let parsed = null;
      try {
        parsed = JSON.parse(payload.arguments || '{}');
      } catch {
        parsed = null;
      }
      const allowedUrl = getAllowedOpenUrl(parsed?.url);

      if (!allowedUrl) {
        sendJson({
          type: 'conversation.item.create',
          item: {
            type: 'function_call_output',
            call_id: callId,
            output: JSON.stringify({ error: 'blocked_url', allowed_prefix: getAllowedOpenUrlPrefix() }),
          },
        });
        sendJson({ type: 'response.create' });
        return;
      }

      cleanupSession();
      window.location.assign(allowedUrl);
    },
    [cleanupSession, sendJson],
  );

  const persistTranscriptPair = useCallback(({ fingerprint, hotelId, locale, onTranscript, transcript }) => {
    if (!transcript || !pendingUserTranscriptRef.current) return;
    const input = pendingUserTranscriptRef.current.trim();
    pendingUserTranscriptRef.current = '';
    const activeLocale = locale || 'en-US';

    if (hotelId && onTranscript) {
      onTranscript({ author: 'user', body: input });
      onTranscript({ author: 'ai', body: transcript });
    }

    Event.publish(EVENT.METRICS, {
      id: 'SYNAPSE_RESPONSE',
      fingerprint,
      text: transcript,
      input,
      agent: 'aura',
      locale: activeLocale,
    });
  }, []);

  const startSession = useCallback(
    async ({
      assistant_name,
      chainId,
      currency,
      hotelId,
      isChain,
      locale,
      onTranscript,
      setIsConnecting,
      setReady,
    }) => {
      cleanupSession();

      const fingerprint = getClientFingerprint();
      const finalizeSession = (error) => {
        const errorMessage = typeof error === 'string' ? error : error?.message;
        if (errorMessage) {
          Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: errorMessage });
        }
        cleanupSession();
        setReady(false);
        setIsConnecting(false);
      };

      try {
        const sessionResponse = await request({
          endpoint: `/voice/session?voiceModel=xai&fingerprint=${fingerprint}&hotelId=${
            isChain ? '' : hotelId ?? ''
          }&chainId=${isChain ? chainId ?? '' : ''}&locale=${locale ?? ''}&currency=${currency ?? ''}`,
          hostname: process.env.SERVICE_SYNAPSE,
          method: 'GET',
        });

        const ephemeralToken = sessionResponse?.value;
        const sessionConfig = sessionResponse?.session;
        const model = sessionResponse?.model || DEFAULT_MODEL;
        if (!ephemeralToken || !sessionConfig) throw new Error('Invalid xAI session response');
        pendingSessionConfigRef.current = sessionConfig;

        const AudioContextConstructor = globalThis.AudioContext || globalThis.webkitAudioContext;
        if (!AudioContextConstructor) throw new Error('AudioContext not supported');
        const audioContext = new AudioContextConstructor({ sampleRate: SAMPLE_RATE });
        audioContextRef.current = audioContext;
        if (audioContext.state === 'suspended') await audioContext.resume();

        const mediaStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
            sampleRate: SAMPLE_RATE,
          },
        });
        localStreamRef.current = mediaStream;

        const inputSource = audioContext.createMediaStreamSource(mediaStream);
        inputSourceRef.current = inputSource;

        const inputAnalyser = audioContext.createAnalyser();
        inputAnalyser.fftSize = 1024;
        inputAnalyserRef.current = inputAnalyser;
        inputSource.connect(inputAnalyser);

        const outputAnalyser = audioContext.createAnalyser();
        outputAnalyser.fftSize = 1024;
        outputAnalyserRef.current = outputAnalyser;
        outputAnalyser.connect(audioContext.destination);

        inputFrequencyDataRef.current = new Uint8Array(inputAnalyser.frequencyBinCount);
        outputFrequencyDataRef.current = new Uint8Array(outputAnalyser.frequencyBinCount);
        audioAnalyzersRef.current.inputAnalyser = inputAnalyser;
        audioAnalyzersRef.current.outputAnalyser = outputAnalyser;
        audioAnalyzersRef.current.getInputByteFrequencyData = () => {
          if (!inputAnalyserRef.current || !inputFrequencyDataRef.current) return null;
          inputAnalyserRef.current.getByteFrequencyData(inputFrequencyDataRef.current);
          return inputFrequencyDataRef.current;
        };
        audioAnalyzersRef.current.getOutputByteFrequencyData = () => {
          if (!outputAnalyserRef.current || !outputFrequencyDataRef.current) return null;
          outputAnalyserRef.current.getByteFrequencyData(outputFrequencyDataRef.current);
          return outputFrequencyDataRef.current;
        };

        // ScriptProcessor is deprecated but still the simplest portable way to obtain
        // raw PCM frames synchronously. It must be connected to the destination to be scheduled;
        // we route it through a muted GainNode so the mic input is never audible locally.
        const processor = audioContext.createScriptProcessor(PROCESSOR_BUFFER_SIZE, 1, 1);
        scriptProcessorRef.current = processor;
        const muteGain = audioContext.createGain();
        muteGain.gain.value = 0;
        inputMuteGainRef.current = muteGain;
        inputSource.connect(processor);
        processor.connect(muteGain);
        muteGain.connect(audioContext.destination);

        processor.onaudioprocess = (event) => {
          if (!microphoneEnabledRef.current) return;
          const ws = wsRef.current;
          if (!ws || ws.readyState !== WebSocket.OPEN) return;
          const channelData = event.inputBuffer.getChannelData(0);
          const int16 = float32ToInt16(channelData);
          ws.send(
            JSON.stringify({
              type: 'input_audio_buffer.append',
              audio: base64FromInt16(int16),
            }),
          );
        };

        const ws = new WebSocket(`${XAI_REALTIME_URL}?model=${encodeURIComponent(model)}`, [
          `xai-client-secret.${ephemeralToken}`,
        ]);
        wsRef.current = ws;

        ws.addEventListener('open', () => {
          Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_START });
          sendJson({ type: 'session.update', session: pendingSessionConfigRef.current });
        });

        ws.addEventListener('error', () => finalizeSession('xAI WebSocket error'));
        ws.addEventListener('close', () => finalizeSession());

        ws.addEventListener('message', async (event) => {
          let payload;
          try {
            payload = JSON.parse(event.data);
          } catch {
            return;
          }

          switch (payload.type) {
            case 'session.created':
            case 'session.updated': {
              if (sessionAppliedRef.current) break;
              sessionAppliedRef.current = true;
              setReady(true);
              sendIntro({ assistantName: assistant_name, locale });
              break;
            }
            case 'response.output_audio.delta': {
              const audio = extractAudioDelta(payload);
              if (audio) scheduleAudioPlayback(audio);
              break;
            }
            case 'input_audio_buffer.speech_started': {
              // Barge-in: cut current playback so the user can take over the turn cleanly.
              stopPendingPlayback();
              break;
            }
            case 'response.function_call_arguments.done': {
              handleFunctionCall(payload);
              break;
            }
            case 'response.done': {
              // Open the microphone once the intro response finishes; subsequent turns use server VAD.
              if (!microphoneEnabledRef.current) microphoneEnabledRef.current = true;
              break;
            }
            case 'conversation.item.input_audio_transcription.completed': {
              const transcript = payload.transcript?.trim();
              if (transcript) pendingUserTranscriptRef.current += transcript;
              break;
            }
            case 'response.output_audio_transcript.done':
            case 'response.text.done': {
              const transcript = (payload.transcript || payload.text)?.trim();
              persistTranscriptPair({ fingerprint, hotelId, locale, onTranscript, transcript });
              break;
            }
            default:
              break;
          }
        });
      } catch (error) {
        Event.publish(EVENT.METRICS, { id: METRICS_EVENTS.AURA_ERROR, error: error?.message });
        setReady(false);
        throw error;
      } finally {
        setIsConnecting(false);
      }
    },
    [
      cleanupSession,
      handleFunctionCall,
      persistTranscriptPair,
      scheduleAudioPlayback,
      sendIntro,
      sendJson,
      stopPendingPlayback,
    ],
  );

  const endSession = useCallback(() => {
    cleanupSession();
  }, [cleanupSession]);

  return { startSession, endSession, audioAnalyzers: audioAnalyzersRef.current };
};

export { useXaiRealtimeSession };
